/** Copyright (c) 2018 T.Rowe Price. All rights reserved. */
import { uniqBy, isNil, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, {Fragment, useContext, useEffect, useState} from 'react';

import ButtonWithLoadingIndicator from '../../shared/ButtonWithLoadingIndicator/ButtonWithLoadingIndicator';
import SecondaryButton from '../../shared/SecondaryButton/SecondaryButton';
import { callApi, COMPOSE, SUCCESS } from '../../utils/api';
import {
  accountTypesLabelMapping,
  collegeSavingsOptions,
  isCollegeSavings,
  messageSubjectOptions,
} from '../../utils/constants';
import {
  appendIdToBodyBySelectedAccountType,
  getMessageToIdBySelectedAccountType,
  replaceBreaksWithBr
} from '../../utils/utils';
import WebViewContext from '../../utils/webview-context';
import ComposeFAQ from '../ComposeFAQ/ComposeFAQ';
import { getFaqItem, RMD_SUBJECT } from './faqUtil';
import styles from './MessageCompositionBody.module.scss';
import { MessageComponentBeaconControl } from "../../shared/BeaconDropdown/BeaconDropdown";


const MessageCompositionBody = ({
  accountTypes = [],
  apiKey,
  baseUrl,
  // customerType,
  env,
  messageFromId,
  openModal,
  transition
}) => {
  const isCustomerWebView = useContext(WebViewContext) || false;
  const [selectedAccountType, setSelectedAccountType] = useState(
    accountTypes.length === 1 ? accountTypes[0] : ''
  );
  const [selectedSubject, setSelectedSubject] = useState('');
  const [message, setMessage] = useState('');
  const [readyToSubmit, setReady] = useState(false);
  //When a new account type is selected, the subject dropdown resets to 'Choose Subject.'
  const [accountIsInvalid, setAccountIsInvalid] = useState(accountTypes.length > 1);
  const [subjectIsInvalid, setSubjectIsInvalid] = useState(true);
  const handleSelectAccountType = event => {
    setSelectedAccountType(event.target.value);
    setSelectedSubject('');
  };

  useEffect(() => {
    if(isNil(selectedAccountType) || isEmpty(selectedAccountType))
      setAccountIsInvalid(true);
    else {
      setAccountIsInvalid(false);
    }

    if(isNil(selectedSubject) || isEmpty(selectedSubject))
      setSubjectIsInvalid(true);
    else
      setSubjectIsInvalid(false);
    setReady(!!selectedAccountType && !!selectedSubject && !!message);
  }, [message, selectedAccountType, selectedSubject]);

  useEffect(() => {
    if (selectedSubject === RMD_SUBJECT) { openModal({ type: 'rmd' }) }
  }, [selectedSubject]);

  const SubjectTypeOptions = (options) => {
    return options && options.map(o => <option value={o.subject} key={o.subject}>{o.subject}</option>);
  }
  const callCompose = async () => {
    const parsedMessage = replaceBreaksWithBr(message);
    const composedMessage = {};

    composedMessage.content = appendIdToBodyBySelectedAccountType(
      parsedMessage,
      selectedAccountType,
      accountTypes
    );
    composedMessage.createdTimestamp = new Date().getTime();
    composedMessage.fromId = messageFromId;
    composedMessage.replyId = message.replyId
      ? message.replyId
      : message.messageId
        ? message.messageId
        : '-1';
    composedMessage.subject = selectedSubject;
    composedMessage.toId = getMessageToIdBySelectedAccountType(selectedAccountType, accountTypes);

    return await callApi(baseUrl, apiKey, COMPOSE, composedMessage);
  };

  const accountTypeOptions = uniqBy(
    accountTypes.map(value => ({
      label: accountTypesLabelMapping[value],
      value
    })),
    'value'
  )

  return (
    <div className={styles['compose-body']}>
      <main className={styles['message-body']}>
        <div className={styles['message-details']}>
          <p className={styles['mobile-header']}>New Message</p>
          {accountTypes.length === 1 && <Fragment>
          <div
            className={styles['dropdown-label']}><span>Account Type:</span>
          </div>
          <div className={styles['read-only-dropdown']}>
            <span>{accountTypeOptions[0].label}</span>

          </div>
          </Fragment>}
          {accountTypes.length > 1 && (<div className={styles['dropdown-row']}>
            <MessageComponentBeaconControl required>
              <label slot="label" htmlFor="account-select">Account Type:</label>
              <select
                  id="account-type-select"
                  data-testid="daccount-type-select"
                  className={styles['dropdown-body']}
                  onChange={(e) => handleSelectAccountType(e)}
                  value={selectedAccountType}
              >
                <option key='default' value='' defaultValue={''} />
                {accountTypeOptions.map(option => <option value={option.value} key={option.value}>{option.label}</option>)}
              </select>
              <div slot="error">Select the account type</div>
            </MessageComponentBeaconControl>
            <span
            className={styles['red-asterisk']}><span>{accountIsInvalid && '*'}</span>
            </span>
          </div>)}
          {!isCollegeSavings(selectedAccountType) && (
            <div className={styles['dropdown-row']}>
              <MessageComponentBeaconControl required>
                <label slot="label" htmlFor="subject-type-select">Subject:</label>
                <select
                    id="subject-type-select"
                    data-testid="dsubject-type-select"
                    className={styles['dropdown-body']}
                    onChange={(e) => setSelectedSubject(e.target.value)}
                    value={selectedSubject}
                >
                  <option key='default' value='' defaultValue={''} />
                  {SubjectTypeOptions(messageSubjectOptions)}
                </select>
                <div slot="error">Select a subject</div>
              </MessageComponentBeaconControl>
              <span
                className={styles['red-asterisk']}><span>{subjectIsInvalid && '*'}</span>
            </span></div>)}
          {isCollegeSavings(selectedAccountType) && (
            <div className={styles['dropdown-row']}>
              <MessageComponentBeaconControl required>
                <label slot="label" htmlFor="college-savings-subject-type-select">Subject:</label>
                <select
                    id="college-savings-subject-type-select"
                    data-testid="dcollege-subject-type-select"
                    className={styles['dropdown-body']}
                    onChange={(e) => setSelectedSubject(e.target.value)}
                    value={selectedSubject}
                >
                  <option key='default' value='' defaultValue={''} />
                  {SubjectTypeOptions(collegeSavingsOptions)}
                </select>
                <div slot="error">Required</div>
              </MessageComponentBeaconControl>
              <span
                className={styles['red-asterisk']}><span>{subjectIsInvalid && '*'}</span>
            </span></div>)}
        </div>
        <textarea
          className={`blocked_data ${styles['message-content']}`}
          onChange={e => setMessage(e.target.value)}
          placeholder="To send a secure email, complete your account type, subject, and message."
          value={message}
        />
        <div className={styles['message-buttons']}>
          <SecondaryButton
            className={styles['cancel-button']}
            id="cancel-message"
            onClick={() => transition('back')}
          >
            Cancel
          </SecondaryButton>
          <ButtonWithLoadingIndicator
            className={`${styles['send-button']} branded-button`}
            disabled={!readyToSubmit || isCustomerWebView}
            id="send-message"
            onClick={async () => {
              const res = await callCompose();
              res.status !== SUCCESS
                ? openModal({ type: 'sendError' })
                : openModal({ type: 'send' });
            }}
          >
            Send
          </ButtonWithLoadingIndicator>
        </div>
      </main>
      <ComposeFAQ
        env={env}
        faqItems={getFaqItem(selectedAccountType, selectedSubject, env)}
        selectedSubject={selectedSubject}
      />
    </div>
  );
};

MessageCompositionBody.propTypes = {
  accountTypes: PropTypes.arrayOf(PropTypes.number),
  apiKey: PropTypes.string,
  baseUrl: PropTypes.string,
  customerType: PropTypes.number,
  env: PropTypes.string,
  messageFromId: PropTypes.string,
  openModal: PropTypes.func,
  transition: PropTypes.func
};

export default MessageCompositionBody;
